import { AfterViewInit, Component, Input } from "@angular/core";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";

import { FiatService } from "@main-services/currency/fiat.service";
import { Helper } from "@helpers/functions";
import { Trade } from "@models";
import { IndexWalletService } from "@main-services/wallet/index_wallet.service";
import { IndexService } from "@main-services/currency/index.service";
import { convertTradeToFiatTransaction, convertTradeToTransaction } from "@helpers/tradable-convert";
import { FiatTransaction, GenericTransaction, Transaction } from "@models/transaction";
import { BehaviorSubject, Observable } from "rxjs";

@Component({
	selector: "index-transaction-list-detail",
	templateUrl: "./index-transaction-list-detail.component.html",
	standalone: false
})
export class IndexTransactionListDetailComponent implements AfterViewInit {
	@Input("transaction")
	private transaction: Transaction = null;
	@Input()
	public showShareButton: boolean = false;

	private indexRebalanceTrades: BehaviorSubject<Transaction[]> = new BehaviorSubject<Transaction[]>([]);
	private indexAddedTrades: BehaviorSubject<Transaction[]> = new BehaviorSubject<Transaction[]>([]);
	private indexRemovedTrades: BehaviorSubject<Transaction[]> = new BehaviorSubject<Transaction[]>([]);

	public constructor(
		private indexWalletService: IndexWalletService,
		private indexService: IndexService,
		private fiatService: FiatService,
		private apicallService: ApicallService
	) {}

	public ngAfterViewInit(): void {
		if (this.transaction.getRelatedTransactions().length > 0) {
			this.indexRebalanceTrades.next(this.transaction.getRelatedTransactions());
		} else {
			// If a regular index-trade has already been opened, this re-fetches the data so no new call is necessary
			this.fetchIndexTrades();
		}
	}

	public getRebalanceTrades$(): Observable<Transaction[]> {
		return this.indexRebalanceTrades.asObservable();
	}

	public getAddedTrades$(): Observable<Transaction[]> {
		return this.indexAddedTrades.asObservable();
	}

	public getRemovedTrades$(): Observable<Transaction[]> {
		return this.indexRemovedTrades.asObservable();
	}

	public getRebalanceTrades(): Transaction[] {
		return this.indexRebalanceTrades.getValue();
	}

	public getAddedTrades(): Transaction[] {
		return this.indexAddedTrades.getValue();
	}

	public getRemovedTrades(): Transaction[] {
		return this.indexRemovedTrades.getValue();
	}

	public hasTrades(transactions: Transaction[]): boolean {
		return transactions.length > 0;
	}

	public isRebalancingTx(): boolean {
		return this.transaction.isRebalancingTx();
	}

	public showFiatTxList(): boolean {
		return this.transaction instanceof FiatTransaction;
	}

	private fetchIndexTrades(): void {
		let url: string = "wallet/" + this.transaction.getWalletId() + "/transactions/" + this.transaction.getId();
		url = url + (this.isRebalancingTx() ? "/rebalance-trades" : "/trades"); // Check which endpoint needs to be accessed depending on the index tx

		const option = new ApicallOption(this.isRebalancingTx() ? RebalancedTradesModel : Trade);
		option.params = {
			page_size: 25
		};
		this.apicallService.get(url, option).subscribe(
			(res) => {
				if (this.isRebalancingTx()) {
					const rebalancedTrades: RebalancedTradesModel = res[0];
					this.indexAddedTrades.next(this.convertIndexTradeToArray(rebalancedTrades.getAddedTrades()));
					this.indexRebalanceTrades.next(
						this.convertIndexTradeToArray(rebalancedTrades.getRebalancedTrades())
					);
					this.indexRemovedTrades.next(this.convertIndexTradeToArray(rebalancedTrades.getRemovedTrades()));
				} else {
					this.transaction.setRelatedTransactions(this.convertIndexTradeToArray(res)); // Setting this on the transaction for some minor performance improvement
					this.indexRebalanceTrades.next(this.convertIndexTradeToArray(res)); // For normal index trades only the RebalanceList is used
				}
			},
			(err) => Helper.handleError(err)
		);
	}

	private convertIndexTradeToArray(trades: Trade[]): Transaction[] {
		const convertedTrades: Transaction[] = [];
		trades.forEach((trade: Trade) => {
			let tx: Transaction = null;
			if (this.showFiatTxList()) {
				tx = convertTradeToFiatTransaction(
					trade,
					new FiatTransaction(),
					this.fiatService.getItemById(trade.getFiatId())
				);
			} else {
				// Used for displaying index in fiat-wallets
				tx = convertTradeToTransaction(
					trade,
					new GenericTransaction(),
					this.fiatService.getItemById(trade.getFiatId())
				);
			}
			convertedTrades.push(tx);
		});
		return convertedTrades;
	}
}

class RebalancedTradesModel {
	private added_trades: Trade[] = [new Trade()];
	private rebalanced_trades: Trade[] = [new Trade()];
	private removed_trades: Trade[] = [new Trade()];

	public getAddedTrades(): Trade[] {
		if (this.added_trades[0].isNew()) {
			return [];
		}
		return this.added_trades;
	}

	public getRebalancedTrades(): Trade[] {
		if (this.rebalanced_trades[0].isNew()) {
			return [];
		}
		return this.rebalanced_trades;
	}

	public getRemovedTrades(): Trade[] {
		if (this.removed_trades[0].isNew()) {
			return [];
		}
		return this.removed_trades;
	}
}
